// src/components/DataRequestForm.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import DataDisplay from './DataDisplay';
import { CURRENT_YEAR_STRING } from '../utils/constants';

function DataRequestForm({
  allPossibleEndpoints,        // Object: { displayName: { path, method, requiresYearAll, endpointCode, parameters } }
  isRequestInProgress,
  onSubmit,
  responseMessage,
  isErrorResponseMessage,
  noDataInfoMessage,
  decodedToken,
  user,
  userEndpoints
}) {
  // Build the list of endpoints allowed by the user's endpoints from CSV
  const availableEndpoints = useMemo(() => {
    if (!allPossibleEndpoints) return [];
    const names = Object.keys(allPossibleEndpoints);
  
    if (!Array.isArray(userEndpoints) || userEndpoints.length === 0) return [];
    
    return names.filter((name) => {
      const ep = allPossibleEndpoints[name];
      return !ep?.endpointCode || userEndpoints.includes(ep.endpointCode);
    });
  }, [allPossibleEndpoints, userEndpoints]);

  // Current selection
  const [selectedEndpointName, setSelectedEndpointName] = useState(
    availableEndpoints.length > 0 ? availableEndpoints[0] : ''
  );

  // Selected endpoint object
  const selectedEndpointObj = useMemo(() => {
    if (!selectedEndpointName || !allPossibleEndpoints) return null;
    return allPossibleEndpoints[selectedEndpointName] || null;
  }, [selectedEndpointName, allPossibleEndpoints]);

  // Derive year/format options from the endpoint
  const yearOptions = useMemo(() => {
    if (!selectedEndpointObj?.parameters?.year) return [];
    const paramCfg = selectedEndpointObj.parameters.year;
    const numericYearsDesc = [...(paramCfg.values || [])]
      .map((y) => parseInt(y, 10))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => b - a)
      .map(String);

    return paramCfg.allowAll ? [...numericYearsDesc, 'all'] : numericYearsDesc;
  }, [selectedEndpointObj]);

  const formatOptions = useMemo(() => {
    if (!selectedEndpointObj?.parameters?.format) return [];
    const formats = selectedEndpointObj.parameters.format.values || [];
    return formats.map((f) => String(f).toLowerCase());
  }, [selectedEndpointObj]);

  // Local state for year/format
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR_STRING);
  const [dataFormat, setDataFormat] = useState('json');

  // Ensure a valid endpoint selection when the available list changes
  useEffect(() => {
    if (availableEndpoints.length === 0) {
      if (selectedEndpointName !== '') setSelectedEndpointName('');
      return;
    }
    if (!availableEndpoints.includes(selectedEndpointName)) {
      setSelectedEndpointName(availableEndpoints[0]);
    }
  }, [availableEndpoints, selectedEndpointName]);

  // Sync year/format when the endpoint changes
  useEffect(() => {
    const endpoint = selectedEndpointObj;
    if (!endpoint) {
      setSelectedYear('');
      setDataFormat('');
      return;
    }

    // Year defaulting
    const yearCfg = endpoint.parameters?.year;
    if (yearCfg) {
      const numericVals = (yearCfg.values || []).map(String);
      let opts = [...numericVals];
      if (yearCfg.allowAll && !opts.includes('all')) {
        opts = [...opts, 'all'];
      }

      const defaultYear = opts.includes(CURRENT_YEAR_STRING)
        ? CURRENT_YEAR_STRING
        : (opts[0] || '');

      if (!opts.includes(String(selectedYear))) {
        setSelectedYear(defaultYear);
      } else if (selectedYear === '' && defaultYear !== '') {
        setSelectedYear(defaultYear);
      }
    } else {
      if (selectedYear !== '') setSelectedYear('');
    }

    // Format defaulting
    const fmtCfg = endpoint.parameters?.format;
    if (fmtCfg) {
      const fmtOpts = (fmtCfg.values || []).map((f) => String(f).toLowerCase());
      const currentFmt = String(dataFormat).toLowerCase();
      const defaultFmt = fmtOpts[0] || '';
      if (!fmtOpts.includes(currentFmt)) {
        setDataFormat(defaultFmt);
      } else if (dataFormat === '' && defaultFmt !== '') {
        setDataFormat(defaultFmt);
      }
    } else {
      if (dataFormat !== '') setDataFormat('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEndpointName]);

  // Keep year valid if options change
  useEffect(() => {
    if (yearOptions.length > 0 && !yearOptions.includes(String(selectedYear))) {
      setSelectedYear(yearOptions[0]);
    }
    if (yearOptions.length === 0 && selectedYear) {
      setSelectedYear('');
    }
  }, [yearOptions, selectedYear]);

  // Keep format valid if options change
  useEffect(() => {
    const fmt = String(dataFormat).toLowerCase();
    if (formatOptions.length > 0 && !formatOptions.includes(fmt)) {
      setDataFormat(formatOptions[0]);
    }
    if (formatOptions.length === 0 && dataFormat) {
      setDataFormat('');
    }
  }, [formatOptions, dataFormat]);

  // Build a dynamic filename based on the endpoint path
  const getDynamicFilename = useCallback(({ dataFormat: fmt, selectedYear: year }) => {
    const format = (fmt || 'json').toLowerCase();
    const path = selectedEndpointObj?.path || '';

    if (path === 'v1/datasets/missions') {
      return `${year}_AggregateMissionCount.${format}`;
    }

    if (path === 'v1/datasets/volunteers/metadata') {
      return `${year}_volunteers_metadata.${format}`;
    }

    if (path === 'v1/datasets/volunteers') {
      return `${year}_volunteers.${format}`;
    }

    if (path === 'v1/bulk/datasets/volunteers/request') {
      return `bulk_volunteers_${year}.${format}`;
    }

    if (path === 'v1/datasets/unsecretariat') {
      return `${year}_unsecretariat.${format}`;
    }

    if (path === 'v1/datasets/volunteers_costing_unov') {
      return `${year}_volunteer_costing.${format}`;
    }

    const safeName = (selectedEndpointName || 'download').toLowerCase().replace(/\s+/g, '_');
    if (!selectedEndpointObj?.parameters?.year || !year) {
      return `${safeName}.${format}`;
    }
    return `${safeName}_${year}.${format}`;
  }, [selectedEndpointName, selectedEndpointObj]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!selectedEndpointObj) return;
    onSubmit({
      selectedEndpoint: selectedEndpointObj,
      dataFormat,
      selectedYear,
      getDynamicFilename
    });
  };

  return (
    <div className="content-container">
      <h3>Welcome</h3>
      <p>Select the data you want to download.</p>

      <form onSubmit={handleFormSubmit}>
        <div>
          <label htmlFor="endpoint-select">Select the endpoint:</label>
          <select
            id="endpoint-select"
            className="select-styled"
            value={selectedEndpointName}
            onChange={(e) => setSelectedEndpointName(e.target.value)}
            disabled={availableEndpoints.length === 0 || isRequestInProgress}
          >
            {availableEndpoints.length === 0 && <option value="">No endpoints available</option>}
            {availableEndpoints.map((endpointName) => (
              <option key={endpointName} value={endpointName}>{endpointName}</option>
            ))}
          </select>
        </div>

        {selectedEndpointObj?.parameters?.year && (
          <div>
            <label htmlFor="year-select">Select the year:</label>
            <select
              id="year-select"
              className="select-styled"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              disabled={isRequestInProgress || !selectedEndpointName || yearOptions.length === 0}
            >
              {yearOptions.length === 0
                ? <option value="">No years available</option>
                : yearOptions.map((year) => (<option key={year} value={year}>{year}</option>))}
            </select>
          </div>
        )}

        {selectedEndpointObj?.parameters?.format && (
          <div>
            <label htmlFor="format-select">Select the data format:</label>
            <select
              id="format-select"
              className="select-styled"
              value={dataFormat}
              onChange={(e) => setDataFormat(e.target.value)}
              disabled={isRequestInProgress || !selectedEndpointName || formatOptions.length === 0}
            >
              {formatOptions.length === 0
                ? <option value="">No formats available</option>
                : formatOptions.map((format) => (<option key={format} value={format}>{format.toUpperCase()}</option>))}
            </select>
          </div>
        )}

        <button
          type="submit"
          disabled={
            isRequestInProgress ||
            !selectedEndpointName ||
            (selectedEndpointObj?.parameters?.year && (yearOptions.length === 0 || !selectedYear)) ||
            (selectedEndpointObj?.parameters?.format && (formatOptions.length === 0 || !dataFormat))
          }
          style={{ marginTop: '20px' }}
        >
          {isRequestInProgress ? "Requesting Data..." : "Download"}
        </button>
      </form>

      <DataDisplay
        responseMessage={responseMessage}
        isErrorResponseMessage={isErrorResponseMessage}
        noDataInfoMessage={noDataInfoMessage}
      />
    </div>
  );
}

DataRequestForm.propTypes = {
  allPossibleEndpoints: PropTypes.object,
  isRequestInProgress: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  responseMessage: PropTypes.string,
  isErrorResponseMessage: PropTypes.bool,
  noDataInfoMessage: PropTypes.string,
  userEndpoints: PropTypes.arrayOf(PropTypes.string),
};

export default DataRequestForm;
